<!--

  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.

-->

<template>
  <div
    class="appointment"
    :class="classes"
    :style="{ ...layout.styles, ...colors }"
    :data-left="layout.attributes.left"
    :data-width="layout.attributes.width"
  >
    <div
      role="button"
      class="appointment-content"
      @click.stop="onClick"
      @mousedown.stop="onDrag"
      :aria-label="ariaLabel"
      :title="title"
    >
      <div v-if="typeFlags.length" class="title-container" aria-hidden="true" :title="title">
        <BaseIcon
          v-for="flag in typeFlags"
          :key="flag.className"
          class="bi-14 my-auto"
          :class="flag.className"
          :iconName="flag.icon"
          :title="flag.title"
          :aria-label="flag.title"
        />
        <div class="title">{{ appointment.summary }}</div>
      </div>
      <div v-else class="title">{{ appointment.summary }}</div>
      <div v-if="appointment.location" class="location">{{ appointment.location }}</div>
      <div
        v-if="propertyFlags.length"
        class="flags shrink-0"
        :class="{'bottom-right': !props.isFullDay}"
      >
        <span>
          <BaseIcon
            v-for="flag in propertyFlags"
            :key="flag.className"
            class="bi-14"
            :class="flag.className"
            :iconName="flag.icon"
            :title="flag.title"
            :aria-label="flag.title"
          />
        </span>
      </div>
    </div>
    <!-- drag handles -->
    <div v-if="isDesktop && classes.modify" class="resizable-handle resizable-n" aria-hidden="true" @mousedown.stop="onDrag"></div>
    <div v-if="isDesktop && classes.modify" class="resizable-handle resizable-s" aria-hidden="true" @mousedown.stop="onDrag"></div>
  </div>
</template>

<script setup>
import ox from '@/ox'
import _ from '@/underscore'
import { computed, inject, ref, toRefs, watch } from 'vue'
import { DateTime } from 'luxon'
import BaseIcon from '@/io.ox/vueComponents/base-icon.vue'
import calendarApi from '@/io.ox/calendar/api'
import folderAPI from '@/io.ox/core/folder/api'
import * as util from '@/io.ox/calendar/util'
import { settings as calendarSettings } from '@/io.ox/calendar/settings'
import gt from 'gettext'
import { ISO_FORMAT } from '@/io.ox/calendar/week/util'
import { updateAppointment } from '@/io.ox/calendar/update-appointment'

const props = defineProps({
  appointment: { type: Object, required: true },
  columnLayout: Object,
  dateRef: { type: Object, required: true },
  isFullDay: { type: Boolean, default: false }
})

const { appointment } = toRefs(props)

const onClickAppointment = inject('onClickAppointment')

const isDesktop = _.device('desktop')
const folder = computed(() => folderAPI.pool.getModel(appointment.value.folder).toJSON())

// local copies for drag support
const start = ref(DateTime.fromISO(appointment.value.startDate.value, { zone: appointment.value.startDate.tzid }))
const end = ref(DateTime.fromISO(appointment.value.endDate.value, { zone: appointment.value.endDate.tzid }))
const moving = ref(false)

watch(appointment, newAppointment => {
  if (moving.value) return
  start.value = DateTime.fromISO(newAppointment.startDate.value, { zone: newAppointment.startDate.tzid })
  end.value = DateTime.fromISO(newAppointment.endDate.value, { zone: newAppointment.endDate.tzid })
})

const layout = computed(() => {
  let left, width
  let column, columnSpan, total
  if (props.columnLayout) {
    const { firstColumn, lastColumn, totalColumns } = props.columnLayout
    column = firstColumn
    total = totalColumns
    columnSpan = lastColumn - firstColumn + 1
    const gap = lastColumn < totalColumns ? '2px' : '0px'
    left = `calc((100% - var(--weekview-trailing-space)) * ${column} / ${totalColumns} - 1px)`
    width = `calc((100% - var(--weekview-trailing-space)) * ${columnSpan} / ${totalColumns} - ${gap})`
  }
  return {
    // to make it testable
    attributes: {
      left: column || 0,
      width: total ? columnSpan / total : 0
    },
    styles: {
      left,
      width,
      top: getTopPropertyFromTime(start.value),
      height: getHeightProperty(start.value, end.value),
      'align-items': props.isFullDay ? 'center' : 'unset',
      opacity: moving.value ? 0.8 : null,
      'z-index': moving.value ? 1 : null
    }
  }
})

const darkTheme = ref(document.querySelector('html').classList.contains('dark'))
ox.on('themeChange', () => {
  darkTheme.value = document.querySelector('html').classList.contains('dark')
})

const derivedColors = computed(() => {
  const baseColor = util.getAppointmentColor(folder.value, appointment.value)
  return util.deriveAppointmentColors(baseColor, darkTheme.value)
})
const colors = computed(() => {
  return {
    color: derivedColors.value.foreground,
    'background-color': derivedColors.value.background,
    'border-inline-start-color': derivedColors.value.border
  }
})

const propertyFlags = computed(() => util.returnIconsByType(appointment.value, true).property)
const typeFlags = computed(() => util.returnIconsByType(appointment.value, true).type)
const title = computed(() => {
  const locationString = appointment.value.location ? `, ${appointment.value.location}` : ''
  return appointment.value.summary + locationString
})
const ariaLabel = computed(() => `${title.value}, ${gt('Category')}: ${derivedColors.value.name}`)

function getTopPropertyFromTime (start) {
  if (props.isFullDay) return
  return start < props.dateRef ? 0 : `${getPositionFromTime(start) * 100}%`
}

function getHeightProperty (start, end) {
  if (props.isFullDay) return

  const bottomPadding = '2px'
  const top = start < props.dateRef ? 0 : getPositionFromTime(start)
  const bottom = end >= props.dateRef.endOf('day') ? 1 : getPositionFromTime(end)

  return `calc(${(bottom - top) * 100}% - ${bottomPadding})`
}

function getPositionFromTime (time) {
  const minutes = time.diff(props.dateRef).as('minutes')
  return minutes / 24 / 60
}

const classes = computed(() => {
  const lightTheme = colors.value.background === 'white'
  const isPrivate = util.isPrivate(appointment.value)
  const modify = folderAPI.can('write', folder.value, appointment.value) && util.allowedToEdit(appointment.value, folder.value)
  const confirmation = util.hasParticipationStatus(appointment.value)
    ? { [util.getConfirmationStatusFromAppointment(appointment.value)]: true }
    : {}

  return {
    white: lightTheme,
    black: !lightTheme,
    private: isPrivate,
    [util.getShownAsClass(appointment.value)]: true,
    [util.getStatusClass(appointment.value)]: true,
    modify,
    ...confirmation
  }
})

function onClick (e) {
  // ignore "clicks" (mousedown) on resizable-handle
  if (e.target.classList.contains('resizable-handle')) return
  if (moving.value) return
  onClickAppointment(appointment.value)
}

function onDrag (e) {
  if (!isDesktop) return
  if (props.isFullDay) return
  if (!classes.value.modify) return
  const original = {
    startDate: start.value,
    endDate: end.value,
    duration: end.value.diff(start.value, 'minutes').toObject()
  }
  let dragging = false
  const firstY = e.clientY
  const offsetY = Math.round(e.pageY - e.currentTarget.getBoundingClientRect().top)
  const container = e.target.closest('.scrollpane')
  const cellSize = container.querySelector('.timeslot').offsetHeight
  const rect = container.getBoundingClientRect()
  const interval = calendarSettings.get('interval', 30)
  const mode = getDragMode(e.target.classList)

  container.addEventListener('mousemove', onMouseMove)
  container.addEventListener('keydown', onKeyDown)
  window.addEventListener('mouseup', onMouseUp)

  function onMouseUp () {
    if (dragging) {
      const { folder, id, recurrenceId } = appointment.value
      calendarApi.get({ folder, id, recurrenceId }).then(async (model) => {
        const result = await updateAppointment(model, {
          startDate: { value: start.value.toFormat(ISO_FORMAT), tzid: appointment.value.startDate.tzid },
          endDate: { value: end.value.toFormat(ISO_FORMAT), tzid: appointment.value.endDate.tzid }
        })
        if (result === 'reset') undoChanges()
      })
    }
    onResizeEnd()
  }

  function undoChanges () {
    start.value = original.startDate
    end.value = original.endDate
  }

  function onResizeEnd () {
    setTimeout(() => { moving.value = false }, 100)
    container.removeEventListener('mousemove', onMouseMove)
    container.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('mouseup', onMouseUp)
  }

  function onKeyDown (e) {
    if (e.key !== 'Escape') return
    undoChanges()
    onResizeEnd()
  }

  function onMouseMove (e) {
    if (!dragging && Math.abs(firstY - e.pageY) <= 10) return
    dragging = true
    const y = Math.max(0, e.pageY - rect.top) + container.scrollTop - (mode === 'move' ? offsetY : 0)
    switch (mode) {
      case 'up': {
        const minutes = Math.floor(y / cellSize) * interval
        const date = original.startDate.startOf('day').plus({ minutes })
        if (date < original.endDate) start.value = date
        break
      }
      case 'down': {
        const minutes = Math.ceil(y / cellSize) * interval
        const date = original.endDate.startOf('day').plus({ minutes })
        if (date > original.startDate) end.value = date
        break
      }
      case 'move': {
        const minutes = Math.round(y / cellSize) * interval
        const date = original.startDate.startOf('day').plus({ minutes })
        start.value = date
        end.value = date.plus(original.duration)
      }
    }
    moving.value = true
  }
}

function getDragMode (classList) {
  if (classList.contains('resizable-n')) return 'up'
  if (classList.contains('resizable-s')) return 'down'
  return 'move'
}

</script>

<style lang="scss" scoped>
.appointment {
  height: calc(4.16667% - 1px);
  inset-inline: -1px 4px;
  width: unset;
}
.resizable-handle {
  position: absolute;
  font-size: 0.1px;
  display: block;
  width: 100%;
  left: 0;
}
.resizable-n {
  cursor: n-resize;
  height: 7px;
  top: -3px;
}
.resizable-s {
  cursor: s-resize;
  height: 7px;
  bottom: -3px;
}
</style>
